import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppNotification } from "../domain/notification.types";

export type NotificationsStatus = "idle" | "loading" | "succeeded" | "failed";

export interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  status: NotificationsStatus;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  status: "idle",
  error: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    notificationsRequestStarted(state) {
      state.status = "loading";
      state.error = null;
    },
    notificationsLoaded(state, action: PayloadAction<AppNotification[]>) {
      state.notifications = action.payload;
      state.status = "succeeded";
    },
    notificationsRequestFailed(state, action: PayloadAction<string>) {
      state.status = "failed";
      state.error = action.payload;
    },
    unreadCountLoaded(state, action: PayloadAction<number>) {
      state.unreadCount = action.payload;
    },
    /** Pushed live over the socket - prepended so it shows up first, same as a fresh fetch would order it. */
    notificationReceived(state, action: PayloadAction<AppNotification>) {
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
    },
    notificationMarkedRead(state, action: PayloadAction<AppNotification>) {
      const index = state.notifications.findIndex((n) => n.id === action.payload.id);
      const wasUnread = index !== -1 && !state.notifications[index]!.isRead;
      if (index !== -1) {
        state.notifications[index] = action.payload;
      }
      if (wasUnread) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    allNotificationsMarkedRead(state) {
      state.notifications = state.notifications.map((n) => ({ ...n, isRead: true }));
      state.unreadCount = 0;
    },
  },
});

export const {
  notificationsRequestStarted,
  notificationsLoaded,
  notificationsRequestFailed,
  unreadCountLoaded,
  notificationReceived,
  notificationMarkedRead,
  allNotificationsMarkedRead,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
